import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BrandSelector from "@components/Features/PostAdv/BrandSelector/BrandSelector";
import ButtonSelector from "@components/Features/PostAdv/ButtonSelector/ButtonSelector";
import ImgSelector from "@components/Features/PostAdv/ImgSelector/ImgSelector";
import {
    ImgSelectorOption,
    ImgSelectorProps
} from "@components/Features/PostAdv/ImgSelector/types";
import ListSelector from "@components/Features/PostAdv/ListSelector/ListSelector";
import ModelSelector from "@components/Features/PostAdv/ModelSelector/ModelSelector";
import MaterialTextInput from "@components/UI/Buttons/MaterialTextInput/MaterialTextInput";
import { NOT_FILLED, numberWithSpaces } from "@src/utils/utils";
import {
    setBodyType,
    setGeneration,
    setOwnersQnt,
    setSelectedDrive,
    setSelectedEngine,
    setSelectedEngineDisplacement,
    setSelectedMileage,
    setSelectedPts,
    setSelectedTransmission,
    setYear
} from "@app/store/analyticsFormSlice";
import {
    useGetBodyTypesOptionsQuery,
    useGetDisplacementsOptionsQuery,
    useGetDriveTypesOptionsQuery,
    useGetEngineTypesOptionsQuery,
    useGetGenerationsOptionsQuery,
    useGetTransmissionOptionsQuery
} from "@app/store/filtersOptionsApi";
import { AppState } from "@app/store/store";
import { Generation } from "@app/store/types";
import { CURRENT_YEAR, START_YEAR_PRODUCTION } from "@shared/config/Date";
import { useFallbackQuery } from "@shared/lib/hooks/useFallbackQuery";
import { generateYearsOptions, numberToRoman } from "@shared/lib/utils";
import { YearOption } from "@shared/lib/utils/generateYearsOptions/types";
import Characteristic, { NOT_VALID_MSG } from "../../PostAdv/Characteristic/Characteristic";
import { AnalyticsFromProps } from "./types";
import classes from "./analytics-form.module.scss";

const AnalyticsForm = ({
    isEditing = false,
    isValidationsVisible,
    ...props
}: AnalyticsFromProps) => {
    const storeName = "analyticsForm";
    const dispatch = useDispatch();
    const selectedBrand = useSelector((state: AppState) => state[storeName].selectedBrand);
    const selectedModel = useSelector((state: AppState) => state[storeName].selectedModel);

    const selectedEngine = useSelector((state: AppState) => state[storeName].selectedEngine);
    const selectedDrive = useSelector((state: AppState) => state[storeName].selectedDrive);
    const selectedTransmission = useSelector(
        (state: AppState) => state[storeName].selectedTransmission
    );
    const selectedWheel = useSelector((state: AppState) => state[storeName].selectedWheel);
    const selectedColor = useSelector((state: AppState) => state[storeName].selectedColor);

    const selectedMileage = useSelector((state: AppState) => state[storeName].selectedMileage);

    const state = useSelector((state: AppState) => state[storeName]);
    const {
        selectedYearUpdated,
        selectedYearValid,

        selectedBodyTypeUpdated,
        selectedBodyTypeValid,

        mileageUpdated,
        mileageValid,

        selectedGenerationUpdated,

        selectedEngineUpdated,
        selectedEngineValid,

        selectedDriveUpdated,
        selectedDriveValid,

        selectedTransmissionUpdated,
        selectedTransmissionValid,

        selectedEngineDisplacement,
        selectedPts,
        selectedEngineDisplacementValid,
        selectedPtsValid,

        ownerQnt,
        ownerQntValid
    } = state;

    const showYearErrors = isValidationsVisible || selectedYearUpdated;
    const showMileageErrors = isValidationsVisible || mileageUpdated;
    const showSelectedBodyTypeErrors = isValidationsVisible || selectedBodyTypeUpdated;
    const showGenerationErrors = isValidationsVisible || selectedGenerationUpdated;
    const showEngineErrors = isValidationsVisible || selectedEngineUpdated;
    const showDriveErrors = isValidationsVisible || selectedDriveUpdated;
    const showTransmissionErrors = isValidationsVisible || selectedTransmissionUpdated;

    const selectedGeneration = useSelector(
        (state: AppState) => state[storeName].selectedGeneration
    );
    const [showModelSelector, setShowModelSelector] = useState<boolean>(false);
    useEffect(() => {
        if (selectedBrand && !selectedModel) {
            setShowModelSelector(true);
        } else {
            setShowModelSelector(false);
        }
    }, [selectedBrand, selectedModel]);

    const showPropsSelector = selectedBrand && selectedModel && selectedGeneration;
    const [_isAutoSelected, setAutoSelected] = useState<boolean>(false);

    const selectedBodyType = useSelector((state: AppState) => state[storeName].selectedBodyType);
    const selectedYear = useSelector((state: AppState) => state[storeName].selectedYear);
    const { yearErrMessage } = useSelector((state: AppState) => state[storeName]);

    const [yearValue, setYearValue] = useState<number | null>(null);
    useEffect(() => {
        if (selectedYear) {
            setYearValue(selectedYear);
        }
    }, [selectedYear]);

    const {
        data: generationsData,
        isLoading: generationsIsLoading,
        isFetching: generationsIsFetching
    } = useGetGenerationsOptionsQuery(
        {
            model_uuid: selectedModel?.id
        },
        {
            skip: !selectedModel?.id
        }
    );
    const isGenerationsLoading = generationsIsFetching || generationsIsLoading;

    const generationFormattedOptions: ImgSelectorOption[] = generationsData
        ? generationsData.map((gen: Generation) => {
              const { id, name, generation, restyleNumber, startedAt, endedAt } = gen;

              const validStartedAt =
                  startedAt !== undefined && startedAt >= START_YEAR_PRODUCTION
                      ? startedAt
                      : undefined;
              const validEndedAt =
                  endedAt !== undefined && endedAt >= START_YEAR_PRODUCTION
                      ? endedAt
                      : CURRENT_YEAR;

              const period = validStartedAt ? `${validStartedAt} - ${validEndedAt}` : "";
              const generationNum = generation ? numberToRoman(generation) : "";
              const restyle = (restyleNumber || 0) > 0 ? `${restyleNumber} Рестайлинг` : "";

              const parts = [
                  period,
                  generationNum ? `${generationNum}` : "",
                  name ? `${name}` : "",
                  restyle
              ]
                  .filter((part) => part)
                  .join(" ");

              return { label: parts.trim(), id: id, imgUrl: null };
          })
        : [];

    const skipQuery = !selectedBrand?.id || !selectedModel?.id || !selectedGeneration?.id;

    const {
        data: bodyOptions,
        isLoading: bodyOptionsIsLoading,
        isFetching: bodyOptionsIsFetching
    } = useFallbackQuery(
        useGetBodyTypesOptionsQuery,
        {
            brandUuid: selectedBrand?.id,
            modelUuid: selectedModel?.id,
            generationUuid: selectedGeneration?.id
        },
        { skip: skipQuery }
    );
    const isBodyLoading = bodyOptionsIsLoading || bodyOptionsIsFetching;

    const {
        data: engineTypesData,
        isLoading: engineTypesIsLoading,
        isFetching: engineTypesIsFetching
    } = useFallbackQuery(
        useGetEngineTypesOptionsQuery,
        {
            brandUuid: selectedBrand?.id,
            modelUuid: selectedModel?.id,
            generationUuid: selectedGeneration?.id
        },
        { skip: skipQuery }
    );
    const isEngineTypeLoading = engineTypesIsLoading || engineTypesIsFetching;

    const { data: driveTypesData } = useFallbackQuery(
        useGetDriveTypesOptionsQuery,
        {
            brandUuid: selectedBrand?.id,
            modelUuid: selectedModel?.id,
            generationUuid: selectedGeneration?.id
        },
        {
            skip: skipQuery
        }
    );

    const { data: transmissionsData } = useFallbackQuery(
        useGetTransmissionOptionsQuery,
        {
            brandUuid: selectedBrand?.id,
            modelUuid: selectedModel?.id,
            generationUuid: selectedGeneration?.id
        },
        { skip: skipQuery }
    );

    const { data: displacementsOptions } = useFallbackQuery(
        useGetDisplacementsOptionsQuery,
        {
            brandUuid: selectedBrand?.id,
            modelUuid: selectedModel?.id,
            generationUuid: selectedGeneration?.id
        },
        { skip: skipQuery }
    );

    useEffect(() => {
        const isSet = [
            selectedMileage,
            selectedColor,
            selectedDrive,
            selectedEngine,
            selectedTransmission,
            selectedWheel,
            selectedGeneration,
            selectedBodyType,
            selectedYear
        ].every((el) => {
            return !!el === true;
        });

        if (isSet) {
            props.onBlockFilled("characteristics", true);
        } else {
            props.onBlockFilled("characteristics", false);
        }
    }, [
        selectedMileage,
        selectedColor,
        selectedDrive,
        selectedEngine,
        selectedTransmission,
        selectedWheel,
        selectedGeneration,
        selectedBodyType,
        selectedYear
    ]);

    useEffect(() => {
        props.showPropsChanged(!!selectedBrand && !!selectedModel);

        if (!!selectedBrand && !!selectedModel) {
            setAutoSelected(true);

            props.onBlockFilled("auto", true);
        } else {
            props.onBlockFilled("auto", false);
        }
    }, [selectedBrand, selectedModel]);

    const [years, setYears] = useState<YearOption[]>([]);

    useEffect(() => {
        setYears(generateYearsOptions(generationsData || []));
    }, [generationsData]);

    const brandName = selectedBrand ? selectedBrand.name : "Выберите марку автомобиля";
    const fullCarName = selectedModel ? `${brandName} ${selectedModel.name}` : brandName;

    const selectedLabelGeneration = selectedGeneration?.id
        ? generationFormattedOptions.find((option) => option.id === `${selectedGeneration.id}`)
              ?.label
        : undefined;

    const isGenerationValid = !showGenerationErrors || Boolean(selectedLabelGeneration);

    const handleSelectGeneration: ImgSelectorProps["onSelect"] = (selectedLabel) => {
        const selectedOption = generationFormattedOptions.find(
            (option) => option.label === selectedLabel
        );
        if (selectedOption) {
            const fullGenerationData = generationsData.find(
                (gen: Generation) => gen.id === selectedOption.id
            );
            if (fullGenerationData) {
                dispatch(setGeneration(fullGenerationData));
            }
        }
    };

    return (
        <div id="post-ad-form-select-auto" className={classes.analyticsFrom}>
            <h2>{fullCarName}</h2>

            <BrandSelector storeName="analyticsForm" />

            <ModelSelector
                storeName="analyticsForm"
                selectedBrand={!!selectedBrand}
                showModelSelector={showModelSelector}
            />

            {!!selectedBrand && !!selectedModel && (
                <Characteristic
                    noDivider={true}
                    title="Поколение"
                    value={selectedLabelGeneration}
                    isValid={isGenerationValid}
                    notValidLabel={"не выбрано"}
                >
                    <ImgSelector
                        selected={selectedLabelGeneration}
                        onSelect={handleSelectGeneration}
                        options={generationFormattedOptions}
                        isLoading={isGenerationsLoading}
                    />
                </Characteristic>
            )}

            {showPropsSelector && (
                <div
                    style={{
                        paddingTop: "1em"
                    }}
                    id="post-ad-form-characteristics"
                >
                    <h2>Характеристики</h2>
                    <Characteristic
                        isValid={showYearErrors ? selectedYearValid : true}
                        notValidLabel={yearErrMessage || NOT_VALID_MSG}
                        title={"Год выпуска"}
                        value={selectedYear?.toString()}
                    >
                        <MaterialTextInput
                            value={
                                selectedYear
                                    ? selectedYear.toString()
                                    : yearValue
                                      ? yearValue?.toString()
                                      : ""
                            }
                            onChange={(val) => {
                                setYearValue(parseInt(val));
                                dispatch(setYear(parseInt(val)));
                            }}
                            label="Год"
                            style={{
                                marginBottom: 30
                            }}
                            maxLength={4}
                        />

                        <ListSelector
                            onItemSelected={(item) => {
                                setYearValue(parseInt(item.value));
                                dispatch(setYear(parseInt(item.value)));
                            }}
                            options={years.filter((year) => {
                                if (isEditing) {
                                    if (yearValue && yearValue.toString().length === 4) {
                                        return year;
                                    }
                                } else {
                                    if (selectedYear) {
                                        return year;
                                    }
                                }

                                return yearValue
                                    ? year.value.toString().includes(yearValue?.toString() || "")
                                    : year;
                            })}
                        />
                    </Characteristic>

                    <Characteristic
                        isValid={showSelectedBodyTypeErrors ? selectedBodyTypeValid : true}
                        title="Кузов"
                        value={selectedBodyType}
                        notValidLabel={"не выбран"}
                    >
                        <ImgSelector
                            isLoading={isBodyLoading}
                            selected={selectedBodyType}
                            onSelect={(item) => {
                                dispatch(setBodyType(item));
                            }}
                            options={
                                bodyOptions
                                    ? bodyOptions.map((el: string, index: number) => {
                                          return {
                                              label: el,
                                              id: index,
                                              imgUrl: undefined
                                          };
                                      })
                                    : []
                            }
                        />
                    </Characteristic>

                    <Characteristic
                        title="Двигатель"
                        value={selectedEngine}
                        isValid={showEngineErrors ? selectedEngineValid : true}
                        notValidLabel={"не выбран"}
                    >
                        <ButtonSelector
                            isLoading={isEngineTypeLoading}
                            options={
                                engineTypesData
                                    ? engineTypesData.map((el: string, index: number) => {
                                          return {
                                              label: el,
                                              id: index
                                          };
                                      })
                                    : []
                            }
                            selected={selectedEngine}
                            onSelect={(item) => {
                                dispatch(setSelectedEngine(item));
                            }}
                        />
                    </Characteristic>

                    <Characteristic
                        title="Привод"
                        value={selectedDrive}
                        isValid={showDriveErrors ? selectedDriveValid : true}
                        notValidLabel={"не выбран"}
                    >
                        <ButtonSelector
                            options={
                                driveTypesData
                                    ? driveTypesData.map((el: string, index: number) => {
                                          return {
                                              label: el,
                                              id: index
                                          };
                                      })
                                    : []
                            }
                            selected={selectedDrive}
                            onSelect={(item) => {
                                dispatch(setSelectedDrive(item));
                            }}
                        />
                    </Characteristic>

                    <Characteristic
                        title="Коробка передач"
                        value={selectedTransmission}
                        isValid={showTransmissionErrors ? selectedTransmissionValid : true}
                        notValidLabel={"не выбрана"}
                    >
                        <ButtonSelector
                            options={
                                transmissionsData
                                    ? transmissionsData.map((el: string, index: number) => {
                                          return {
                                              label: el,
                                              id: index
                                          };
                                      })
                                    : []
                            }
                            selected={selectedTransmission}
                            onSelect={(item) => {
                                dispatch(setSelectedTransmission(item));
                            }}
                        />
                    </Characteristic>

                    <Characteristic
                        title="Птс"
                        isValid={selectedPtsValid}
                        notValidLabel={NOT_FILLED || NOT_VALID_MSG}
                        value={selectedPts}
                    >
                        <ButtonSelector
                            options={[
                                {
                                    label: "Оригинал",
                                    id: 1
                                },
                                {
                                    label: "Дубликат",
                                    id: 2
                                }
                            ]}
                            selected={selectedPts}
                            onSelect={(item) => {
                                dispatch(setSelectedPts(item));
                            }}
                        />
                    </Characteristic>

                    <Characteristic
                        title="Объем двигателя, л"
                        isValid={selectedEngineDisplacementValid}
                        notValidLabel={NOT_FILLED || NOT_VALID_MSG}
                        value={selectedEngineDisplacement}
                    >
                        <ButtonSelector
                            options={
                                displacementsOptions
                                    ? displacementsOptions.map((item: string) => ({ label: item }))
                                    : []
                            }
                            selected={selectedEngineDisplacement}
                            onSelect={(item) => {
                                dispatch(setSelectedEngineDisplacement(item));
                            }}
                        />
                    </Characteristic>

                    <Characteristic
                        noDivider
                        title="Пробег"
                        value={selectedMileage ? `${selectedMileage} км` : ""}
                        isValid={showMileageErrors ? mileageValid : true}
                        notValidLabel={NOT_FILLED}
                    >
                        <MaterialTextInput
                            value={numberWithSpaces(selectedMileage || "").toString()}
                            label="Пробег, км"
                            onChange={(val) => {
                                dispatch(setSelectedMileage(parseInt(val.replace(/\D+/g, ""))));
                            }}
                            maxLength={8}
                        />
                    </Characteristic>

                    <Characteristic
                        noDivider
                        title="Кол-во владельцев"
                        value={ownerQnt}
                        isValid={ownerQntValid}
                        notValidLabel={NOT_FILLED}
                    >
                        <MaterialTextInput
                            value={numberWithSpaces(ownerQnt || "").toString()}
                            label="Кол-во"
                            onChange={(val) => {
                                dispatch(setOwnersQnt(parseInt(val.replace(/\D+/g, ""))));
                            }}
                            maxLength={2}
                        />
                    </Characteristic>
                </div>
            )}
        </div>
    );
};

export default AnalyticsForm;